import { Controller } from "@hotwired/stimulus";

// Tableau de taux d'un `TauxTreso` — gère un nombre dynamique de lignes
// (ajout / suppression / édition inline) et auto-sauvegarde l'état dans le
// hidden input parent à chaque changement, sans bouton « Sauvegarder ».
//
// Format JSON conservé compatible avec le legacy `hexTableauDynamique` :
//   [
//     [{ "nom": "type", "result": "Taux" }, { "nom": "base", ... }, ...],
//     [...]
//   ]
//
// Markup attendu :
//   <div data-controller="taux-treso-table"
//        data-taux-treso-table-columns-value='[{"key":"type","type":"select",...}]'>
//     <table class="treso-taux-table__inner">
//       <thead>...</thead>
//       <tbody data-taux-treso-table-target="body"></tbody>
//     </table>
//     <button type="button" data-action="click->taux-treso-table#addRow">…</button>
//     <input type="hidden" data-taux-treso-table-target="hidden"
//            name="taux_treso[table_taux_bv]" value="[]" />
//   </div>
//
// Chaque colonne :
//   { key, label, type: "select"|"text"|"number", options?: [], placeholder?: "" }
export default class extends Controller {
  static targets = ["body", "hidden"];
  static values  = { columns: Array };

  connect() {
    let initial = [];
    try {
      const raw = (this.hiddenTarget.value || "").trim();
      if (raw && raw !== "nil") initial = JSON.parse(raw);
    } catch (_e) { initial = []; }

    if (!Array.isArray(initial)) initial = [];

    // Garde au moins une ligne vide à l'ouverture pour faciliter la saisie.
    if (initial.length === 0) initial = [this._blankRow()];

    this._render(initial);
    this._sync();
  }

  // ── Actions utilisateur ─────────────────────────────────────────────

  addRow(event) {
    event?.preventDefault?.();
    const rows = this._readRowsFromDom();
    rows.push(this._blankRow());
    this._render(rows);
    this._sync();
  }

  removeRow(event) {
    event?.preventDefault?.();
    const idx = parseInt(event.currentTarget.dataset.rowIndex, 10);
    const rows = this._readRowsFromDom();
    if (Number.isInteger(idx) && idx >= 0 && idx < rows.length) {
      rows.splice(idx, 1);
    }
    if (rows.length === 0) rows.push(this._blankRow());
    this._render(rows);
    this._sync();
  }

  // Appelée par les listeners `input` / `change` posés au render.
  syncOnInput() {
    this._sync();
  }

  // ── Rendu ────────────────────────────────────────────────────────────

  _render(rows) {
    const cols = this.columnsValue;
    this.bodyTarget.innerHTML = "";

    rows.forEach((row, idx) => {
      const tr = document.createElement("tr");
      tr.className = "treso-taux-table__row";
      tr.dataset.rowIndex = idx;

      cols.forEach((col) => {
        const td = document.createElement("td");
        td.className = "treso-taux-table__cell";
        td.dataset.colKey = col.key;
        const cell = (Array.isArray(row) ? row : []).find((c) => c.nom === col.key);
        const rawValue = cell ? String(cell.result ?? "") : "";
        // Underscores → espaces (legacy) ; conversion décimal→% pour les
        // colonnes `percent: true` (cf. _toDisplay).
        const displayValue = this._toDisplay(col, rawValue.replace(/_/g, " "));
        td.innerHTML = this._inputHtml(col, displayValue);
        tr.appendChild(td);
      });

      const tdRm = document.createElement("td");
      tdRm.className = "treso-taux-table__cell treso-taux-table__cell--actions";
      tdRm.innerHTML =
        `<button type="button" class="treso-taux-table__row-rm" ` +
        `data-action="click->taux-treso-table#removeRow" ` +
        `data-row-index="${idx}" ` +
        `title="Supprimer cette ligne" aria-label="Supprimer cette ligne">` +
        `<i class="fa-solid fa-xmark"></i></button>`;
      tr.appendChild(tdRm);

      this.bodyTarget.appendChild(tr);
    });

    // Pas besoin d'attacher d'écouteurs ici : la vue déclare sur le root
    // `data-action="input->taux-treso-table#syncOnInput change->…"` qui
    // délègue automatiquement aux inputs/selects nouvellement injectés.
    // On synchronise une fois après render pour que le hidden input soit
    // alimenté immédiatement (utile au tout 1er rendu d'une nouvelle ligne).
    this._sync();
  }

  _inputHtml(col, value) {
    const safeVal = this._esc(value);
    const placeholder = this._esc(col.placeholder || "");

    switch (col.type) {
      case "select": {
        const opts = (col.options || []).map((o) => {
          const oLabel = String(o).replace(/_/g, " ");
          const oValue = String(o);
          const selected =
            oValue === value || oLabel === value || this._esc(oLabel) === safeVal
              ? "selected"
              : "";
          return `<option value="${this._esc(oValue)}" ${selected}>${this._esc(oLabel)}</option>`;
        }).join("");
        return `<select class="treso-taux-table__input">` +
               `<option value="">—</option>${opts}</select>`;
      }
      case "number":
        return `<input type="number" step="any" class="treso-taux-table__input" ` +
               `value="${safeVal}" placeholder="${placeholder}" />`;
      default:
        return `<input type="text" class="treso-taux-table__input" ` +
               `value="${safeVal}" placeholder="${placeholder}" />`;
    }
  }

  // ── Sérialisation ───────────────────────────────────────────────────

  _readRowsFromDom() {
    const cols = this.columnsValue;
    const rows = [];
    this.bodyTarget.querySelectorAll(".treso-taux-table__row").forEach((rowEl) => {
      const rowData = cols.map((col) => {
        const cellEl = rowEl.querySelector(`[data-col-key="${col.key}"]`);
        const input = cellEl?.querySelector("input, select");
        const displayed = input?.value ?? "";
        // Conversion %→décimal pour les colonnes `percent: true`.
        return { nom: col.key, result: this._toStored(col, displayed) };
      });
      rows.push(rowData);
    });
    return rows;
  }

  _sync() {
    const rows = this._readRowsFromDom();
    // Le legacy stockait avec underscores au lieu d'espaces — on garde ce
    // format pour rester compatible avec les calculs côté serveur.
    const normalized = rows.map((row) =>
      row.map((c) => ({ nom: c.nom, result: String(c.result || "").replace(/ /g, "_") }))
    );
    this.hiddenTarget.value = JSON.stringify(normalized);
  }

  _blankRow() {
    return this.columnsValue.map((c) => ({ nom: c.key, result: "" }));
  }

  // Décimal stocké → pourcentage affiché (ex: "0.105" → "10.5").
  // Précision arrondie à 6 chiffres pour gommer les flottants foireux
  // (10.499999… → 10.5).
  _toDisplay(col, rawStored) {
    if (!col.percent) return rawStored;
    if (rawStored === "" || rawStored == null) return "";
    const num = Number(rawStored);
    if (!Number.isFinite(num)) return rawStored;
    return String(Math.round(num * 100 * 1000000) / 1000000);
  }

  // Pourcentage saisi → décimal stocké (ex: "10.5" → "0.105").
  _toStored(col, rawDisplayed) {
    if (!col.percent) return rawDisplayed;
    if (rawDisplayed === "" || rawDisplayed == null) return "";
    const num = Number(rawDisplayed);
    if (!Number.isFinite(num)) return rawDisplayed;
    return String(Math.round((num / 100) * 1000000) / 1000000);
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }
}
